/** RefCellEditor — the L2 popup cell editor for entity-ref columns (person/organization).
 *
 * Extracted from CrmListGrid (D2b) into its own module so the record panel can re-host it vertically
 * without importing CrmListGrid (which imports the panel → a circular import). The grid re-exports it
 * for back-compat (`export { RefCellEditor } from './CrmRefCellEditor'`), so existing imports/tests
 * keyed on `./CrmListGrid` keep resolving. Behavior is byte-identical to the pre-extraction version.
 */
import { useEffect, useRef, useState } from 'react';
import type { CustomCellEditorProps } from 'ag-grid-react';
import styles from './crm.module.css';
import type { CrmLookupResult, CrmRefValue } from '../../types';
import { isRef } from './crmCells';
import { refLabel } from './EntityRefs';
import { cancelEditor, EditorCloseButton } from './crmEditorCancel';
import { CrmOrgCreateInline } from './CrmOrgCreateInline';
import { useCrmLookup } from '../../hooks/useCrm';

const _normalizeRefs = (v: unknown): CrmRefValue[] => (Array.isArray(v) ? v : [v]).filter(isRef);

/**
 * Current refs as removable chips + a debounced directory typeahead (POST /crm/lookup) to add.
 * Single (person/organization) types REPLACE; multi types append, deduped by local_id. Commits
 * `[{local_id,name}]` (multi) / a single `{local_id,name}` dict / null via onValueChange — the caller
 * PATCHes it, and the backend re-resolves names + validates entitlement (the client value is never
 * trusted as identity).
 *
 * INLINE CREATE (`allowCreate`, organisations only) closes the dead end this editor had: the directory
 * can only offer a company that has emailed us, and the moment of need is exactly the moment one has
 * not — a deal's target that has never sent us a message could not be named at all. When the search
 * settles on no matches, `CrmOrgCreateInline` offers to create it and then ADDS the new entity here,
 * so the flow continues without a second search. **Opt-in per host**, so the grid's cell editors are
 * byte-identical: only the fact-add picker passes it today.
 *
 * PR3 item 8: ✕ closes WITHOUT changes, restoring the value the editor opened with — see
 * `crmEditorCancel`, which owns the two hosts' discard mechanisms.
 */
export function RefCellEditor(
  props: CustomCellEditorProps & {
    kind?: 'person' | 'organization'; single?: boolean; onCancelEdit?: () => void;
    /** Offer "create it" when the organisation search finds nothing. Admin-gated inside the affordance. */
    allowCreate?: boolean },
) {
  const { value, onValueChange, stopEditing } = props;
  const kind = props.kind ?? 'person';
  const single = !!props.single;
  const allowCreate = !!props.allowCreate;
  const [creating, setCreating] = useState(false);
  const initial = useRef(value);   // the value the editor OPENED with — what ✕ restores
  const [refs, setRefs] = useState<CrmRefValue[]>(() => _normalizeRefs(value));
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  useEffect(() => { const t = setTimeout(() => setDq(q), 250); return () => clearTimeout(t); }, [q]);
  const lookup = useCrmLookup(kind, dq);

  const commit = (next: CrmRefValue[]) => {
    setRefs(next);
    onValueChange(single ? (next[0] ?? null) : next);
  };
  const add = (r: CrmLookupResult) => {
    const ref: CrmRefValue = { local_id: r.id, name: r.name };
    if (single) commit([ref]);
    else if (!refs.some((x) => x.local_id === r.id)) commit([...refs, ref]);
    setQ('');
  };
  const results = (lookup.data?.results ?? []).filter((r) => !refs.some((x) => x.local_id === r.id));

  return (
    <div className={styles.refEditor}>
      <div className={styles.refEditorChips}>
        {refs.length === 0 && <span className={styles.gridEmpty}>none</span>}
        {/* E2c: a staged chip is labelled by governed NAME. A ref with no name is one this viewer
            may not see, and printing `#<local_id>` handed over the exact id the server withheld — in
            an EDITOR, where it would then be committed straight back. Such a ref shows an opaque
            placeholder that is still REMOVABLE, so an operator is never stuck unable to clear a cell
            they cannot read. */}
        {refs.map((r) => {
          // `refLabel` knows the difference between "you may not see this" and "this person has no
          // name on record" — an earlier version called both "Restricted", which told operators they
          // lacked permission they had.
          const label = refLabel(r) ?? 'Restricted';
          return (
            <span key={r.local_id} className={styles.gridRef}>
              {label}
              <button className={styles.refEditorRemove} aria-label={`Remove ${label}`}
                      onClick={() => commit(refs.filter((x) => x.local_id !== r.local_id))}>×</button>
            </span>
          );
        })}
      </div>
      <input className={styles.refEditorSearch} autoFocus value={q} placeholder={`Search ${kind}…`}
             onChange={(e) => setQ(e.target.value)} />
      <div className={styles.refEditorResults}>
        {q.trim().length < 2 && <span className={styles.gridEmpty}>Type at least 2 characters.</span>}
        {/* "No matches" only once the debounce has SETTLED on the current input (dq === live q) —
            else it flashes for ~250ms after a pick clears q, or while typing before dq catches up. */}
        {q.trim().length >= 2 && dq === q && results.length === 0 && !lookup.isFetching && (
          <span className={styles.gridEmpty}>No matches.</span>
        )}
        {results.map((r) => (
          <button key={r.id} className={styles.refEditorResult} onClick={() => add(r)}>
            + {r.name || `#${r.id}`}{r.detail ? <span className={styles.refEditorDetail}> · {r.detail}</span> : null}
          </button>
        ))}
      </div>
      {/* The create affordance appears only once the SEARCH HAS SETTLED on nothing (`dq === q`, not
          fetching) — offering it mid-typeahead would put "create a duplicate" in front of an operator
          whose match had not loaded yet. Organisations only: a hand-made PERSON is a separate,
          unbuilt capability with its own governance questions (a person is an access principal). */}
      {allowCreate && kind === 'organization' && q.trim().length >= 2 && dq === q
        && results.length === 0 && refs.length === 0 && !lookup.isFetching && (
        creating
          ? <CrmOrgCreateInline
              typed={q}
              onCreated={(e) => { setCreating(false); add({ kind: 'organization', id: e.id, name: e.name }); }}
              onCancel={() => setCreating(false)} />
          : <button className={styles.orgCreateOpen} onClick={() => setCreating(true)}>
              + Create “{q.trim()}”
            </button>
      )}
      <div className={styles.refEditorFooter}>
        <EditorCloseButton onClick={() => cancelEditor(props, initial.current)} />
        <button className={styles.gridPageBtn} onClick={() => stopEditing()}>Done</button>
      </div>
    </div>
  );
}
